import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import IndicatorActionSection from './IndicatorActionSection';

/**
 * SubdomainAccordion — collapsible subdomain section with its indicators.
 *
 * Props:
 *   subdomainName  - string
 *   subdomainId    - string
 *   indicators     - [item, ...] (action plan items filtered to this subdomain)
 *   defaultOpen    - bool
 */
const SubdomainAccordion = ({ subdomainName, subdomainId, indicators = [], defaultOpen = false }) => {
    const [open, setOpen] = useState(defaultOpen);

    // Count level distribution for dot indicators
    const levelCounts = indicators.reduce((acc, item) => {
        const lvl = Math.round(item.average_level || item.current_level || 2);
        acc[lvl] = (acc[lvl] || 0) + 1;
        return acc;
    }, {});

    const dotColors = {
        1: 'bg-red-400',
        2: 'bg-amber-400',
        3: 'bg-blue-400',
        4: 'bg-green-400',
    };

    const actionCount = indicators.filter(i => i.plan?.recommendations?.length).reduce((s, i) => s + i.plan.recommendations.length, 0);

    return (
        <div className="rounded-xl border border-gray-100 overflow-hidden">
            {/* Header */}
            <button
                onClick={() => setOpen(p => !p)}
                className="w-full flex items-center gap-3 px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer text-left"
            >
                <span className="text-gray-400 flex-shrink-0 transition-transform" style={{ transform: open ? 'rotate(0deg)' : 'rotate(-90deg)' }}>
                    <ChevronDown className="w-4 h-4" />
                </span>
                <p className="flex-1 text-sm font-semibold text-gray-700">{subdomainName}</p>

                {/* Level dots */}
                <div className="flex items-center gap-1 flex-shrink-0">
                    {[1, 2, 3, 4].map(lvl => levelCounts[lvl] ? (
                        <span key={lvl} className={`w-2 h-2 rounded-full ${dotColors[lvl]}`} title={`${levelCounts[lvl]} at level ${lvl}`} />
                    ) : null)}
                    <span className="text-xs text-gray-400 ml-1">{indicators.length} indicator{indicators.length !== 1 ? 's' : ''}</span>
                    {actionCount > 0 && (
                        <span className="text-xs font-semibold text-[#D67C2C] bg-orange-50 border border-orange-100 px-1.5 py-0.5 rounded ml-1">
                            {actionCount} actions
                        </span>
                    )}
                </div>
            </button>

            {/* Indicators */}
            {open && (
                <div className="p-3 space-y-2">
                    {indicators.map(item => (
                        <IndicatorActionSection
                            key={item.indicator_id}
                            item={item}
                            defaultOpen={(item.average_level || item.current_level || 2) <= 2}
                        />
                    ))}
                    {indicators.length === 0 && (
                        <p className="text-xs text-gray-400 text-center py-3">No action items for this subdomain.</p>
                    )}
                </div>
            )}
        </div>
    );
};

export default SubdomainAccordion;
